import { useState } from "react";
import { toast } from "sonner";
import { joinWaitlist, COMPANY_SIZES } from "@/lib/waitlist";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

export default function WaitlistForm({
    source,
    showCompanySize = false,
    testIdPrefix = "hero",
    buttonLabel = "Join the Alpha",
    compact = false,
    inputTestId,
    submitTestId,
    sizeTestId,
    formTestId,
    successTestId,
}) {
    const [email, setEmail] = useState("");
    const [companySize, setCompanySize] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error("Please enter your work email.");
            return;
        }
        setSubmitting(true);
        try {
            await joinWaitlist({ email, company_size: companySize, source });
            setDone(true);
            toast.success("You're in. Check your inbox for confirmation.");
            setEmail("");
            setCompanySize("");
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Something went wrong. Try again.";
            toast.error(typeof msg === "string" ? msg : "Something went wrong.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div
                data-testid={successTestId || `${testIdPrefix}-success`}
                className="flex items-center gap-3 rounded-xl border border-[#10B981]/30 bg-[#10B981]/5 px-4 py-3.5 text-[14px]"
            >
                <span className="h-6 w-6 rounded-full bg-[#10B981] flex items-center justify-center text-black">
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                    >
                        <path
                            d="M2.5 6l2.5 2.5L10 3.5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
                <div>
                    <div className="text-white font-medium">
                        You're on the list.
                    </div>
                    <div className="text-neutral-400 text-[12px]">
                        Confirmation sent. We'll be in touch.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid={formTestId || `${testIdPrefix}-waitlist-form`}
            className={`${compact ? "" : "w-full"}`}
        >
            <div
                className={`flex flex-col ${showCompanySize ? "sm:flex-row" : "sm:flex-row"} gap-2`}
            >
                <div className="relative flex-1 min-w-0">
                    <input
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        required
                        data-testid={inputTestId || `${testIdPrefix}-waitlist-input`}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="w-full h-12 rounded-xl bg-white/[0.04] border border-white/10 px-4 pr-4 text-[14px] text-white placeholder:text-neutral-500 outline-none focus:border-[#10B981]/50 focus:bg-white/[0.06] transition-colors"
                    />
                </div>

                {showCompanySize && (
                    <div className="sm:w-[180px]">
                        <Select
                            value={companySize}
                            onValueChange={setCompanySize}
                        >
                            <SelectTrigger
                                data-testid={sizeTestId || `${testIdPrefix}-company-size`}
                                className="h-12 rounded-xl bg-white/[0.04] border border-white/10 text-[14px] text-white data-[placeholder]:text-neutral-500 focus:border-[#10B981]/50 focus:ring-0 focus:ring-offset-0"
                            >
                                <SelectValue placeholder="Team size" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                                {COMPANY_SIZES.map((s) => (
                                    <SelectItem
                                        key={s.value}
                                        value={s.value}
                                        className="text-[13px] focus:bg-[#10B981]/15 focus:text-white"
                                    >
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={submitting}
                    data-testid={submitTestId || `${testIdPrefix}-waitlist-submit`}
                    className="group relative inline-flex items-center justify-center gap-2 h-12 px-5 rounded-xl bg-[#10B981] hover:bg-[#34D399] disabled:opacity-60 disabled:cursor-not-allowed text-black font-medium text-[14px] transition-all whitespace-nowrap"
                    style={{ boxShadow: "0 6px 28px -6px rgba(16,185,129,0.55)" }}
                >
                    {submitting ? "Joining…" : buttonLabel}
                    {!submitting && (
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            className="transition-transform group-hover:translate-x-0.5"
                        >
                            <path
                                d="M3 7h8m0 0L7 3m4 4l-4 4"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                            />
                        </svg>
                    )}
                </button>
            </div>

            <p className="mt-3 text-[11px] text-neutral-500">
                No spam. SOC-2 roadmap. Unsubscribe anytime.
            </p>
        </form>
    );
}
